// W36: thin wrapper over the generated platform-admin surface.
//
// W2: typed access to `/v1/admin/*`. Every route below requires a JWT with
// `scope=platform` (minted by the W3 platform login); a tenant-scope JWT gets
// HTTP 403 from the server's `PlatformAdminOnly` policy.
//
// This is the one module that is not one service's client. `/v1/admin/*` is a
// gateway-assembled surface owned by ten different services, so the types come
// from ten generated directories, each under its own alias:
//
//   identity    tenants · users · scope · impersonation · api-keys
//   billing     stats · sku-rates · ledger · invoices · plans · subscriptions ·
//               extra use (settings and a block per business)
//   inbox       stats · transfer audit · conversations
//   settings    integrations · alerts · ops (queues / health / backups)
//   contacts    opt-in audit
//   webhooks    delivery audit · subscriptions
//   flows-llm   flow-run audit
//   media       storage stats
//   channel-wa  numbers · templates
//   broadcasts  broadcasts
//
// Regenerate any one of them with
// `pnpm --filter @repletoryreach/sdk-api generate <service>`. The request path
// stays on the house core so the envelope, Idempotency-Key, token refresh and
// ApiError mapping behave exactly as before.
//
// ⚠ Path note: `/v1/admin/integrations` is registered as
// `MapGroup("/v1/admin/integrations")` + `MapGet("/")` (and so is
// `/v1/admin/feature-flags` over in `./feature-flags.ts`), so the ASP.NET route
// pattern carries a trailing slash. The emitted OpenAPI document normalises
// that away: `gen.SettingsAdminListIntegrationsData['url']` reads
// `/v1/admin/integrations`, exactly the string this module has always sent.
// Routing accepts both spellings, so every URL below is left byte-identical to
// what shipped.

import { http } from './_core/request.js';
import type { Envelope, ListWindowMeta, EnvelopeWithMeta } from './_core/envelope.js';
import type {
  ExtraUseView,
  InvoiceStatus,
  InvoiceValidation,
  InvoiceValidationStatus,
  InvoiceValidator
} from './billing.js';
import type * as genIdentity from './generated/identity/index.js';
import type * as genBilling from './generated/billing/index.js';
import type * as genInbox from './generated/inbox/index.js';
import type * as genSettings from './generated/settings/index.js';
import type * as genContacts from './generated/contacts/index.js';
import type * as genWebhooks from './generated/webhooks/index.js';
import type * as genFlows from './generated/flows-llm/index.js';
import type * as genMedia from './generated/media/index.js';
import type * as genChannelWa from './generated/channel-wa/index.js';
import type * as genBroadcasts from './generated/broadcasts/index.js';

// ── Tenants + users (identity) ────────────────────────────────────────────

export type AdminTenant = genIdentity.AdminTenantListItem & {
  /** Self-service closure (2 October 2026): when it closes, when it closed, and whether a refund of unused credit was asked for. */
  closes_at?: string | null;
  closed_at?: string | null;
  refund_requested?: boolean;
  close_reason?: string | null;
};

export type AdminUser = Omit<genIdentity.AdminUserListItem, 'email' | 'scope'> & {
  /** owner, admin or agent in their organisation; absent for platform staff (2 October 2026). */
  org_role?: string | null;
  /** False when the account was switched off (3 October 2026). */
  is_active?: boolean;
  two_factor_enabled?: boolean;
  /** False until an invitation is accepted. */
  email_confirmed?: boolean;
  /**
   * ⚠ DRIFT: `users.email` is a nullable column, and the generated
   * `AdminUserListItem` types it `null | string`. Declared non-null here
   * because the console dereferences it (`u.email.toLowerCase()`, and
   * `ChangeScopeButton`'s `email` prop is a bare `string`), so a null row is a
   * runtime TypeError there rather than a compile error here.
   */
  email: string;
  /**
   * ⚠ DRIFT: `users.scope` is a free-form `text` column and reaches the wire
   * as an unconstrained `string`; nothing narrows it to these two codes. The
   * union is kept because `ChangeScopeButton`'s `current` prop is
   * `'tenant' | 'platform'`: a third value seeded into the column would flow
   * through here unchecked.
   */
  scope: 'tenant' | 'platform';
};

// ── Agreements (2 October 2026) ────────────────────────────────────────────

export interface AdminAgreementVersion {
  version: string;
  summary: string | null;
  published_at: string;
  published_by: string | null;
  accepted_by_organisations: number;
}

export interface AdminAgreement {
  kind: string;
  name: string;
  path: string;
  current_version: string | null;
  active_organisations: number;
  versions: AdminAgreementVersion[];
}

export interface AdminOrganisationAgreements {
  status: import('./identity.js').AgreementStatus[];
  history: {
    kind: string;
    version: string;
    accepted_at: string;
    accepted_by_email: string | null;
    accepted_by_name: string | null;
    accepted_ip: string | null;
    user_agent: string | null;
  }[];
}

/** Each agreement, its published versions and how many organisations accepted each. */
export function listAgreements(): Promise<Envelope<AdminAgreement[]>> {
  return http.get<Envelope<AdminAgreement[]>>('/v1/admin/agreements');
}

/**
 * Publishes a new version, so every organisation is asked to accept again.
 * The wording itself is the website's legal page; `version` is its date.
 * Fails with 409 `VERSION_EXISTS`.
 */
export function publishAgreementVersion(kind: string, req: { version: string; summary?: string }): Promise<Envelope<{ kind: string; version: string; summary: string | null; published_at: string }>> {
  return http.post(`/v1/admin/agreements/${encodeURIComponent(kind)}/versions`, { body: req });
}

/** One organisation's agreement record: where it stands, and every acceptance. */
export function getOrganisationAgreements(orgId: string): Promise<Envelope<AdminOrganisationAgreements>> {
  return http.get<Envelope<AdminOrganisationAgreements>>(`/v1/admin/agreements/organisations/${encodeURIComponent(orgId)}`);
}

export function listTenants(): Promise<Envelope<AdminTenant[]>> {
  return http.get<Envelope<AdminTenant[]>>('/v1/admin/tenants');
}

/**
 * `opts.scope` is narrowed to the two real codes; the generated
 * `IdentityAdminListUsersData['query']` types it as an open `string` (the
 * handler lower-cases whatever it is given and filters `users.scope` on it).
 *
 * `limit` defaults to 500 server-side and is clamped to 1000. `meta.total` is
 * the count for the filter rather than the page, so a footer can say how many
 * users exist instead of how many it managed to fetch.
 */
export function listUsers(opts?: { scope?: 'tenant' | 'platform'; limit?: number }): Promise<EnvelopeWithMeta<AdminUser[], ListWindowMeta>> {
  const q: Record<string, string | number> = {};
  if (opts?.scope) q.scope = opts.scope;
  if (opts?.limit != null) q.limit = opts.limit;
  return http.get<EnvelopeWithMeta<AdminUser[], ListWindowMeta>>('/v1/admin/users', Object.keys(q).length ? { query: q } : undefined);
}

/**
 * The response is the generated `UserScopeUpdatedResponse` (`{id, scope}`),
 * kept inline so the published signature is unchanged. `scope` comes back as
 * the free-form column value, which is why it is a bare `string` here even
 * though the argument is the narrow union.
 */
export function updateUserScope(id: string, scope: 'tenant' | 'platform'): Promise<Envelope<{ id: string; scope: string }>> {
  return http.put<Envelope<{ id: string; scope: string }>>(`/v1/admin/users/${encodeURIComponent(id)}/scope`, { body: { scope } });
}

/**
 * Invite another platform operator (3 October 2026). They set a password through a 7-day link to the
 * platform console and must set up two-factor sign-in before their first session. 409 `ALREADY_STAFF`
 * or `EMAIL_IN_USE` (the address is a customer's).
 */
export function inviteStaff(req: { email: string; display_name?: string }): Promise<Envelope<{ id: string; email: string; display_name: string; scope: string }>> {
  return http.post<Envelope<{ id: string; email: string; display_name: string; scope: string }>>('/v1/admin/staff', { body: req });
}

/**
 * Switch an account off (signed out everywhere, cannot sign in) or back on. 400 `CANNOT_SUSPEND_SELF`;
 * 409 `LAST_PLATFORM_ADMIN` for the only operator who can still sign in.
 */
export function setUserActive(id: string, active: boolean): Promise<Envelope<{ id: string; is_active: boolean }>> {
  return http.put<Envelope<{ id: string; is_active: boolean }>>(`/v1/admin/users/${encodeURIComponent(id)}/active`, { body: { active } });
}

// ── W31: impersonation (identity) ─────────────────────────────────────────

/** A short-lived tenant-scoped JWT (≤15 min, no refresh token) plus its target. */
export type ImpersonationGrant = genIdentity.ImpersonationGrantResponse;

/**
 * One row of the impersonation audit feed.
 *
 * ⚠ DRIFT; this is *not* a projection of a table. The handler re-reads each
 * `admin.impersonation.started` outbox row's JSON payload with `GetString()`,
 * so `tenant_id` and `expires_at` arrive as plain strings (no uuid / date-time
 * format, and `null` whenever the key is missing from the payload). Only
 * `created_at` is a real column.
 */
export type ImpersonationAuditRow = genIdentity.ImpersonationAuditRow;

/** Mint a short-lived tenant-scoped impersonation token for the target org. Audited. */
export function impersonate(tenantId: string): Promise<Envelope<ImpersonationGrant>> {
  return http.post<Envelope<ImpersonationGrant>>('/v1/admin/impersonate', { body: { tenant_id: tenantId } });
}

/** Recent impersonation mints (audit feed; newest first, up to 100). */
export function listImpersonations(): Promise<Envelope<ImpersonationAuditRow[]>> {
  return http.get<Envelope<ImpersonationAuditRow[]>>('/v1/admin/impersonations');
}

// ── Cross-service stats (billing + inbox) ─────────────────────────────────

export type AdminBillingStats = genBilling.AdminBillingStatsDto;
export type AdminInboxStats = genInbox.AdminInboxStats;

export function billingStats(): Promise<Envelope<AdminBillingStats>> {
  return http.get<Envelope<AdminBillingStats>>('/v1/admin/stats/billing');
}

export function inboxStats(): Promise<Envelope<AdminInboxStats>> {
  return http.get<Envelope<AdminInboxStats>>('/v1/admin/stats/inbox');
}

// ── SKU rates + ledger (billing) ──────────────────────────────────────────

/** A price-book row as `GET /v1/admin/sku-rates` projects it. */
/**
 * A price book row. The last four fields are the price ladder (21 September
 * 2026), by hand until the billing types are regenerated: `floor_minor` is the
 * least the row may be listed at (its cost plus the free plan's markup),
 * `list_minor` the list price in force (never under the floor),
 * `follows_ladder` whether the row is priced at the floor itself, and
 * `plan_amounts_minor` what one unit costs on every plan on sale.
 */
export type AdminSkuRate = genBilling.AdminSkuRateDto & {
  floor_minor?: number;
  list_minor?: number;
  follows_ladder?: boolean;
  plan_amounts_minor?: Record<string, number>;
};

/**
 * What `PUT /v1/admin/sku-rates/{id}` actually answers with: the four columns
 * of the newly-inserted version row. See the drift note on
 * {@link updateSkuRate}.
 */
export type AdminSkuRateVersion = genBilling.SkuRateVersionDto;

/** `amount_minor` is the charge; `cost_minor` (optional) the platform's cost. Omitted fields carry over from the current version. */
/**
 * `follow_ladder: true` prices the row at its cost plus the free plan's markup
 * and ignores `amount_minor`. A charge under that floor is refused with 422
 * `RATE_BELOW_FLOOR` (`floor_minor` in the body).
 */
export type UpdateSkuRateRequest = genBilling.UpdateSkuRateRequest & { follow_ladder?: boolean };
export type CreateSkuRateRequest = genBilling.CreateSkuRateRequest;
/** `minute`, `number` and `month` are Reach Voice (2 October 2026): a call minute, a phone number registered, a month of renting one. */
export type SkuUnit = 'message' | '1k_tokens' | '10k_tokens' | 'document' | 'question' | 'file' | 'gb_day' | 'call' | 'minute' | 'number' | 'month' | 'session' | 'code' | '1k_chars' | 'image';
export type SkuCategory = 'whatsapp' | 'ai' | 'knowledge' | 'notifications' | 'media' | 'web' | 'meta' | 'voice';

export type AdminLedgerEntry = genBilling.AdminLedgerEntryDto;

export function listSkuRates(): Promise<Envelope<AdminSkuRate[]>> {
  return http.get<Envelope<AdminSkuRate[]>>('/v1/admin/sku-rates');
}

/**
 * Supersede a SKU rate: the handler deactivates every active row for the same
 * `(sku_code, currency)` and inserts a new version.
 *
 * ⚠ DRIFT: declared `Envelope<AdminSkuRate>` at 200, but the service answers
 * **201 Created** with the narrow {@link AdminSkuRateVersion} projection
 * (`{id, sku_code, amount_minor, effective_from}`), so `name`, `currency` and
 * `is_active` are `undefined` at runtime. The wide type is this module's
 * published return type and is kept; treat the result as
 * `AdminSkuRateVersion` and refetch {@link listSkuRates} for the rest: which
 * is what the console's `useUpdateSkuRate` already does.
 */
export function updateSkuRate(id: string, req: UpdateSkuRateRequest): Promise<Envelope<AdminSkuRate>> {
  return http.put<Envelope<AdminSkuRate>>(`/v1/admin/sku-rates/${encodeURIComponent(id)}`, { body: req });
}

/** A new SKU, or a new currency for an existing one. Answers 201 with the version row. */
export function createSkuRate(req: CreateSkuRateRequest): Promise<Envelope<AdminSkuRateVersion>> {
  return http.post<Envelope<AdminSkuRateVersion>>('/v1/admin/sku-rates', { body: req });
}

// ── Meta rate cards into the price book (15 September 2026) ───────────────

/** Where a price book row stands today: `upcoming` is dated ahead and not priced at yet. */
export type SkuRateStatus = 'current' | 'upcoming' | 'superseded' | 'retired';

/** What an import read and what it would set. Money is rand cents; `meta_rate` is per message in the card's currency. */
/**
 * One line of an import preview. `amount_keep_markup` is the price ladder's
 * figure (the new cost plus the free plan's markup, or a charge set above that,
 * kept); neither suggestion is under `floor_minor`, and applying a charge under
 * it is refused with 422 `RATE_BELOW_FLOOR`. `plan_amounts_minor` is what each
 * plan would pay at the ladder's price.
 */
export type RateImportLine = genBilling.RateImportLineDto & {
  floor_minor?: number;
  plan_amounts_minor?: Record<string, number>;
};
export type RateImportPreview = Omit<genBilling.RateImportPreviewDto, 'lines'> & {
  lines: RateImportLine[];
  entry_markup_percent?: number;
  plans?: { code: string; name: string }[];
};
export type ApplyRateImportRequest = genBilling.ApplyRateImportRequest;
export type ApplyRateImportLine = genBilling.ApplyRateImportLine;
export type RateImportResult = genBilling.RateImportResultDto;
export type SkuRateImport = genBilling.SkuRateImportDto;

export interface RateImportInput {
  /** Meta's CSV, a workbook (`.xlsx`) or a text file. */
  file?: File;
  /** A pasted table, when there is no file. */
  text?: string;
  /** The card's currency; what the file says when omitted, US dollars when it says nothing. */
  currency?: string;
  /** How much of the currency one rand buys; the price book's exchange rate when omitted. */
  per_zar?: number;
  /** Which of the card's markets prices everywhere outside South Africa, Nigeria and Kenya. */
  rest_of_world_market?: string;
  /** YYYY-MM-DD, today or later; today when omitted. */
  effective_from?: string;
}

/**
 * Read a Meta rate card and answer with every WhatsApp rate it would set:
 * Meta's rate, the cost now and new, and two suggested charges (unchanged, or
 * scaled so the markup holds). Writes nothing.
 */
export function previewRateImport(input: RateImportInput): Promise<Envelope<RateImportPreview>> {
  const form = new FormData();
  if (input.file) form.append('file', input.file, input.file.name);
  if (input.text) form.append('text', input.text);
  if (input.currency) form.append('currency', input.currency);
  if (input.per_zar != null) form.append('per_zar', String(input.per_zar));
  if (input.rest_of_world_market) form.append('rest_of_world_market', input.rest_of_world_market);
  if (input.effective_from) form.append('effective_from', input.effective_from);
  return http.post<Envelope<RateImportPreview>>('/v1/admin/sku-rates/import/preview', { body: form });
}

/** Write the confirmed lines, effective today or on a later day. A line equal to the rate in force is left alone. Answers 201. */
export function applyRateImport(req: ApplyRateImportRequest): Promise<Envelope<RateImportResult>> {
  return http.post<Envelope<RateImportResult>>('/v1/admin/sku-rates/import', { body: req });
}

/** Rate cards applied to the price book, newest first. */
export function listRateImports(limit = 10): Promise<Envelope<SkuRateImport[]>> {
  return http.get<Envelope<SkuRateImport[]>>('/v1/admin/sku-rates/imports', { query: { limit: String(limit) } });
}

/** Cancel a rate that has not started. A rate in force answers 409 `NOT_UPCOMING`. */
export function cancelUpcomingSkuRate(id: string): Promise<void> {
  return http.delete<void>(`/v1/admin/sku-rates/${encodeURIComponent(id)}`);
}

// ── FX, margin and wallet alerts (usage metering, 2026-09-05) ─────────────

/**
 * An exchange rate. `per_zar` is the mid-market figure: how many units of the
 * currency one rand buys. Money never converts at it. A customer's payment in
 * the currency converts at `customer_per_zar`, and a cost the platform pays in
 * it (Meta's dollars) at `cost_per_zar`: each is the mid-market rate moved by
 * the spread in the platform's favour. `source` names the feeds that agreed on
 * the figure, or `manual`; `fetched_at` is when a feed last confirmed it. The
 * fields after `updated_by` are by hand until the billing types are regenerated
 * (21 September 2026).
 */
export type FxRate = genBilling.FxRateDto & {
  customer_per_zar?: number;
  cost_per_zar?: number;
  previous_per_zar?: number | null;
  auto_update?: boolean;
  source?: string | null;
  fetched_at?: string | null;
};

/** The exchange rate feed's state. `stale` is a day and a half without a pass in which every currency was confirmed. */
export interface FxMeta {
  spread_percent: number;
  feed_enabled: boolean;
  refresh_hours: number;
  last_run_at?: string | null;
  last_ok_at?: string | null;
  last_error?: string | null;
  stale: boolean;
}

export type FxRatesEnvelope = Envelope<FxRate[]> & { meta?: FxMeta };

export function listFxRates(): Promise<FxRatesEnvelope> {
  return http.get<FxRatesEnvelope>('/v1/admin/fx-rates');
}

/**
 * Set a rate by hand, switch automatic updates for a currency on or off, or
 * both. A number is the old form and still works. With `auto_update: false` the
 * feed leaves the currency alone; left on, the next refresh may replace a figure
 * set by hand.
 */
export function updateFxRate(currency: string, change: number | { per_zar?: number; auto_update?: boolean }): Promise<Envelope<FxRate>> {
  const body = typeof change === 'number' ? { per_zar: change } : change;
  return http.put<Envelope<FxRate>>(`/v1/admin/fx-rates/${encodeURIComponent(currency)}`, { body });
}

/** One currency after a refresh. `outcome`: accepted, sources_disagree, unconfirmed_jump, no_quote or kept_by_hand. */
export interface FxRefreshLine {
  currency: string;
  outcome: 'accepted' | 'sources_disagree' | 'unconfirmed_jump' | 'no_quote' | 'kept_by_hand';
  changed: boolean;
  per_zar?: number | null;
  was_per_zar: number;
  /** What each feed said, by feed name. */
  quotes: Record<string, number>;
  note?: string | null;
}

export interface FxRefresh {
  ran_at: string;
  ok: boolean;
  feeds_reached: string[];
  error?: string | null;
  lines: FxRefreshLine[];
}

/** Read the public exchange rate feeds now instead of waiting for the twelve-hourly pass. A figure no two feeds agree on is not written. */
export function refreshFxRates(): Promise<Envelope<FxRefresh>> {
  return http.post<Envelope<FxRefresh>>('/v1/admin/fx-rates/refresh', { body: {} });
}

/** The spread, in percent (0 to 25): how far the two rates money converts at sit from mid-market, in the platform's favour. */
export function updateFxSettings(spreadPercent: number): Promise<Envelope<FxMeta>> {
  return http.put<Envelope<FxMeta>>('/v1/admin/fx-rates/settings', { body: { spread_percent: spreadPercent } });
}

export interface FxRateChange {
  id: string;
  currency: string;
  per_zar: number;
  previous_per_zar?: number | null;
  source: string;
  quotes: Record<string, number>;
  created_at: string;
  created_by?: string | null;
}

/** Every change of an exchange rate, newest first, by the feed or by hand. */
export function listFxRateHistory(opts?: { currency?: string; limit?: number }): Promise<Envelope<FxRateChange[]>> {
  const q: Record<string, string> = {};
  if (opts?.currency) q.currency = opts.currency;
  if (opts?.limit) q.limit = String(opts.limit);
  return http.get<Envelope<FxRateChange[]>>('/v1/admin/fx-rates/history', Object.keys(q).length ? { query: q } : undefined);
}

/** Revenue against cost per SKU and currency for a window (default: the last 30 days). */
export type AdminUsageMargin = genBilling.AdminUsageMarginDto;
export type AdminMarginSku = genBilling.AdminMarginSkuDto;

export function usageMargin(opts?: { since?: string; until?: string; org_id?: string }): Promise<Envelope<AdminUsageMargin>> {
  const q: Record<string, string> = {};
  if (opts?.since) q.since = opts.since;
  if (opts?.until) q.until = opts.until;
  if (opts?.org_id) q.org_id = opts.org_id;
  return http.get<Envelope<AdminUsageMargin>>('/v1/admin/usage/margin', Object.keys(q).length ? { query: q } : undefined);
}

/**
 * The VAT switch. Off until the platform is registered; on, VAT is added on
 * top of the listed rates for organisations in the registration country from
 * the effective date. VAT is never on without a registration number: enabling
 * without one, or clearing it while on, is refused with 400
 * `VAT_NUMBER_REQUIRED`; a malformed number with `VAT_NUMBER_INVALID`.
 */
export type TaxSettings = genBilling.TaxSettingsDto;
export type UpdateTaxSettingsRequest = genBilling.UpdateTaxSettingsRequest;

export function getTax(): Promise<Envelope<TaxSettings>> {
  return http.get<Envelope<TaxSettings>>('/v1/admin/tax');
}

export function updateTax(req: UpdateTaxSettingsRequest): Promise<Envelope<TaxSettings>> {
  return http.put<Envelope<TaxSettings>>('/v1/admin/tax', { body: req });
}

/**
 * What happens when a plan's card is declined (owner, 11 October 2026): `grace_days` before the plan ends (1 to 60,
 * default 7), `retry_hours` between attempts on the card (1 to 168, default 24), `reminder_days` between reminders
 * (1 to 30, default 2), and `notice_days` before the 1st the "your card will be charged" email goes (0 to 14,
 * default 3; 0 sends none).
 */
export interface RenewalSettings {
  grace_days: number;
  retry_hours: number;
  reminder_days: number;
  notice_days: number;
}

export function getRenewalSettings(): Promise<Envelope<RenewalSettings>> {
  return http.get<Envelope<RenewalSettings>>('/v1/admin/billing/renewal-settings');
}

export function saveRenewalSettings(req: RenewalSettings): Promise<Envelope<RenewalSettings>> {
  return http.put<Envelope<RenewalSettings>>('/v1/admin/billing/renewal-settings', { body: req });
}

// ── Extra use (30 October 2026) ─────────────────────────────────────────────

/**
 * The platform's extra-use settings: whether it is offered at all, the most a
 * business may set as its monthly limit (`max_limit_minor`, 10 000 to
 * 10 000 000, default 500 000 = R5 000, the most one business can owe), the
 * least (`min_limit_minor`, 1 000 to the maximum) and the smallest step or
 * top-up (`min_step_minor`, 1 000 to the maximum). Cents. 400 `VALIDATION`
 * names the field in `error.field`.
 */
export interface ExtraUseSettings {
  offered: boolean;
  max_limit_minor: number;
  min_limit_minor: number;
  min_step_minor: number;
}

export function getExtraUseSettings(): Promise<Envelope<ExtraUseSettings>> {
  return http.get<Envelope<ExtraUseSettings>>('/v1/admin/billing/extra-use-settings');
}

export function saveExtraUseSettings(req: ExtraUseSettings): Promise<Envelope<ExtraUseSettings>> {
  return http.put<Envelope<ExtraUseSettings>>('/v1/admin/billing/extra-use-settings', { body: req });
}

/**
 * One business's extra use, as the business sees it, plus whether the platform
 * blocked it (`platform_blocked`) and why (`blocked_reason`, absent when none).
 */
export type AdminExtraUseView = ExtraUseView & {
  platform_blocked: boolean;
  blocked_reason?: string | null;
};

export function getOrgExtraUse(organisationId: string): Promise<Envelope<AdminExtraUseView>> {
  return http.get<Envelope<AdminExtraUseView>>(`/v1/admin/billing/wallets/${encodeURIComponent(organisationId)}/extra-use`);
}

/**
 * Blocks or unblocks extra use for one business. Blocked, it pauses with
 * `paused_reason: 'blocked'` and spending falls back to the positive balance;
 * what is already owed is still charged. `reason` is optional.
 */
export function setOrgExtraUseBlocked(organisationId: string, blocked: boolean, reason?: string | null): Promise<Envelope<AdminExtraUseView>> {
  const body: { blocked: boolean; reason?: string } = { blocked };
  if (reason && reason.trim()) body.reason = reason.trim();
  return http.put<Envelope<AdminExtraUseView>>(`/v1/admin/billing/wallets/${encodeURIComponent(organisationId)}/extra-use`, { body });
}

/** Mark the platform's own business (a badge and a filter; it is charged like any other). */
export function setHouseAccount(organisationId: string, houseAccount: boolean): Promise<Envelope<{ organisation_id: string; house_account: boolean }>> {
  return http.put<Envelope<{ organisation_id: string; house_account: boolean }>>(
    `/v1/admin/billing/wallets/${encodeURIComponent(organisationId)}/house`, { body: { house_account: houseAccount } });
}

// ── Adding credit by hand (15 October 2026) ─────────────────────────────────

/** `payment`: money received outside the platform (EFT on a manual invoice, cash); `gift`: credit you give; `correction`: credit taken back. */
export type AddCreditKind = 'payment' | 'gift' | 'correction';

export interface AddCreditRequest {
  kind: AddCreditKind;
  /** Cents, R1 to R1 000 000. One rand is one credit. */
  amount_minor: number;
  /** A new id per form, so a retry adds the credit once. */
  request_id: string;
  /** Payment: `eft` (the default), `cash` or `other`. */
  method?: 'eft' | 'cash' | 'other' | null;
  /** Payment: the invoice number or the payment reference. */
  reference?: string | null;
  /** Payment: the day the money arrived, `YYYY-MM-DD`. */
  received_on?: string | null;
  /** Gift or correction: why, shown to the business on its ledger. */
  reason?: string | null;
  /** Payment: email the business its receipt (the default). */
  send_receipt?: boolean | null;
}

export interface AddCreditResult {
  ledger_entry_id: string;
  kind: AddCreditKind;
  amount_minor: number;
  balance_minor: number;
  receipt_sent: boolean;
  /** The same request had already added it; nothing more was added. */
  replayed: boolean;
}

/**
 * Adds credit to a business by hand: a payment made outside the platform (a top-up, with the usual receipt), a gift
 * (a grant) or a correction (taken back, never below what is free). 400 `MORE_THAN_BALANCE` for a correction larger
 * than what is free to take.
 */
export function addCredit(organisationId: string, req: AddCreditRequest): Promise<Envelope<AddCreditResult>> {
  return http.post<Envelope<AddCreditResult>>(`/v1/admin/billing/wallets/${encodeURIComponent(organisationId)}/credit`, { body: req });
}

/** Every wallet that is low, empty, on auto top-up or was recently alerted. */
export type AdminWalletAlert = genBilling.AdminWalletAlertDto;

export function listWalletAlerts(): Promise<genBilling.AdminWalletAlertListResponse> {
  return http.get<genBilling.AdminWalletAlertListResponse>('/v1/admin/wallet-alerts');
}

/** One wallet whatever its state: balance, line, alert settings, auto top-up, month-to-date spend and margin, recent alerts. */
/** `house_account` (11 October 2026): the platform's own business, marked but charged like any other. */
export type AdminWalletDetail = genBilling.AdminWalletDetailDto & { house_account?: boolean; pays_by_invoice?: boolean };

// ── Plans for businesses that pay by invoice (18 October 2026) ────────────────

/** Mark a business as paying by invoice: its plan fee is taken from the bought credit its EFTs are recorded as. */
export function setPaysByInvoice(organisationId: string, paysByInvoice: boolean): Promise<Envelope<{ organisation_id: string; pays_by_invoice: boolean }>> {
  return http.put<Envelope<{ organisation_id: string; pays_by_invoice: boolean }>>(
    `/v1/admin/billing/wallets/${encodeURIComponent(organisationId)}/invoice`, { body: { pays_by_invoice: paysByInvoice } });
}

/** A business's plan as the platform console changes it: the plan, its state and the bought credit a fee can come from. */
export interface OrganisationPlan {
  organisation_id: string;
  pays_by_invoice: boolean;
  /** Bought credit, the part of the balance a plan fee may be taken from. */
  available_minor: number;
  currency: string;
  subscription: {
    id: string;
    /** `active` or `past_due`. */
    status: string;
    plan_code?: string | null;
    plan_name?: string | null;
    billing_cycle_end: string;
    ends_at?: string | null;
    pending_plan_code?: string | null;
    pending_plan_name?: string | null;
  } | null;
}

/**
 * What choosing a plan does now. `action`: `start` (taken now: `amount_minor`), `upgrade` (the part-month difference
 * taken now), `downgrade` (from `effective_at`, the 1st), `cancel` (the paid month runs out, then the free plan),
 * `keep`, `end_now` (a past-due plan moved to the free plan), `past_due` (refused until paid), or `none`.
 */
export interface OrganisationPlanQuote {
  action: 'start' | 'upgrade' | 'downgrade' | 'cancel' | 'keep' | 'end_now' | 'past_due' | 'none';
  plan_code: string;
  plan_name: string;
  current_plan_code?: string | null;
  current_plan_name?: string | null;
  amount_minor: number;
  credits_minor: number;
  effective_at?: string | null;
  available_minor: number;
  /** Whether the bought credit covers `amount_minor`. */
  enough: boolean;
  shortfall_minor: number;
  currency: string;
}

export function getOrganisationPlan(organisationId: string): Promise<Envelope<OrganisationPlan>> {
  return http.get<Envelope<OrganisationPlan>>(`/v1/admin/billing/organisations/${encodeURIComponent(organisationId)}/plan`);
}

export function quoteOrganisationPlan(organisationId: string, planCode: string): Promise<Envelope<OrganisationPlanQuote>> {
  return http.get<Envelope<OrganisationPlanQuote>>(
    `/v1/admin/billing/organisations/${encodeURIComponent(organisationId)}/plan/quote?plan_code=${encodeURIComponent(planCode)}`);
}

/**
 * Choose the plan of a business that pays by invoice. Refused with 409 `NOT_INVOICED` (switch it on first),
 * 409 `PAST_DUE` (add their payment first, or choose the free plan), 402 `INSUFFICIENT_CREDIT` (with `required_minor`,
 * `available_minor`, `shortfall_minor`).
 */
export function chooseOrganisationPlan(organisationId: string, planCode: string): Promise<Envelope<OrganisationPlanQuote>> {
  return http.post<Envelope<OrganisationPlanQuote>>(
    `/v1/admin/billing/organisations/${encodeURIComponent(organisationId)}/plan`, { body: { plan_code: planCode } });
}

export function getWalletAlert(orgId: string): Promise<Envelope<AdminWalletDetail>> {
  return http.get<Envelope<AdminWalletDetail>>(`/v1/admin/wallet-alerts/${encodeURIComponent(orgId)}`);
}

/**
 * Run the monthly usage statement pass now: every organisation with debits in
 * the period (default the previous month) and no statement yet gets one.
 * `org_id` limits it to one tenant.
 */
export function runStatements(opts?: { period?: string; org_id?: string }): Promise<Envelope<genBilling.StatementsRunDto>> {
  const q: Record<string, string> = {};
  if (opts?.period) q.period = opts.period;
  if (opts?.org_id) q.org_id = opts.org_id;
  return http.post<Envelope<genBilling.StatementsRunDto>>('/v1/admin/invoices/statements/run', Object.keys(q).length ? { query: q } : undefined);
}

/**
 * The cross-tenant money journal. `since` and `until` bound the window on the
 * server, so a date range is the real period rather than a re-slice of however
 * many rows the cap returned. The envelope's `meta` carries the true `total`
 * for the window and whether the response was `capped`.
 */
export function listLedger(opts?: { org_id?: string; limit?: number; since?: string; until?: string; currency?: string }): Promise<EnvelopeWithMeta<AdminLedgerEntry[], ListWindowMeta>> {
  const q: Record<string, string | number> = {};
  if (opts?.org_id) q.org_id = opts.org_id;
  if (opts?.limit !== undefined) q.limit = opts.limit;
  if (opts?.since) q.since = opts.since;
  if (opts?.until) q.until = opts.until;
  if (opts?.currency) q.currency = opts.currency;
  return http.get<EnvelopeWithMeta<AdminLedgerEntry[], ListWindowMeta>>('/v1/admin/ledger', Object.keys(q).length ? { query: q } : undefined);
}

// ── Audit feeds (inbox · contacts · webhooks · flows-llm) ──────────────────

export type AdminTransfer = genInbox.AdminTransferDto;

/**
 * The same payload `contacts.OptInEvent` wraps`/v1/admin/audit/opt-in` is
 * mapped by `services/contacts`, and both modules publish a name for it.
 * `source` is `null | string` on the wire (this module used to declare it
 * non-null).
 */
export type AdminOptInEvent = genContacts.OptInEventDto;

/**
 * One webhook delivery attempt. `blocked_reason` (W45 SSRF refusals) is
 * returned by the endpoint and was missing from this client's hand-written
 * type before the cutover; it is present now that the shape is generated.
 */
export type AdminDelivery = genWebhooks.AdminDeliveryDto;

export type AdminFlowRun = genFlows.AdminFlowRunDto;

export function listTransfers(since?: string): Promise<Envelope<AdminTransfer[]>> {
  return http.get<Envelope<AdminTransfer[]>>('/v1/admin/audit/transfers', since ? { query: { since } } : undefined);
}

export function listOptInEvents(since?: string): Promise<Envelope<AdminOptInEvent[]>> {
  return http.get<Envelope<AdminOptInEvent[]>>('/v1/admin/audit/opt-in', since ? { query: { since } } : undefined);
}

/**
 * Webhook delivery attempts across every tenant. Filter by `result`, the
 * readable code (`pending`, `success`, `failed`, `dead`, `blacklisted`,
 * `blocked`), rather than by the lookup id. A `blocked` attempt is one the
 * SSRF guard refused: it has no HTTP status and is not a pending delivery.
 */
export function listDeliveries(opts?: {
  since?: string; until?: string; result?: string; result_id?: string;
  org_id?: string; subscription_id?: string; limit?: number;
}): Promise<EnvelopeWithMeta<AdminDelivery[], ListWindowMeta>> {
  const q: Record<string, string | number> = {};
  if (opts?.since) q.since = opts.since;
  if (opts?.until) q.until = opts.until;
  if (opts?.result) q.result = opts.result;
  if (opts?.result_id) q.result_id = opts.result_id;
  if (opts?.org_id) q.org_id = opts.org_id;
  if (opts?.subscription_id) q.subscription_id = opts.subscription_id;
  if (opts?.limit !== undefined) q.limit = opts.limit;
  return http.get<EnvelopeWithMeta<AdminDelivery[], ListWindowMeta>>('/v1/admin/audit/deliveries', Object.keys(q).length ? { query: q } : undefined);
}

export function listFlowRuns(since?: string): Promise<Envelope<AdminFlowRun[]>> {
  return http.get<Envelope<AdminFlowRun[]>>('/v1/admin/audit/flow-runs', since ? { query: { since } } : undefined);
}

// ── Integrations registry (settings) ──────────────────────────────────────
// External-service credentials (Meta, Bedrock, PayFast, …) live in the
// settings service. The admin UI renders one card per provider catalog row;
// `field_schema` drives the form.

/**
 * Hand-written: `field_schema` is a `jsonb` column the handler hands back
 * un-typed (see {@link IntegrationProvider}), so there is no OpenAPI schema to
 * generate either of these from. They describe the shape the seeded rows use
 * and the shape `Shape()`'s password-masking pass reads.
 */
export type IntegrationFieldKind = 'string' | 'password' | 'url' | 'enum';

export interface IntegrationFieldSchema {
  key: string;
  label: string;
  kind: IntegrationFieldKind;
  required?: boolean;
  description?: string;
  options?: string[];
}

export type IntegrationProvider = Omit<genSettings.IntegrationProviderDto, 'field_schema'> & {
  /**
   * ⚠ DRIFT: generated as `unknown`, and that is the honest type: the handler
   * emits `ParseJson(p.FieldSchema)`, a raw `jsonb` passthrough that can be an
   * object, an array, or (when `JsonDocument.Parse` throws) the stored
   * string verbatim. Declared as the array here because the console's
   * integration drawer calls `.filter()` / `.map()` on it directly; a provider
   * row seeded with a malformed or non-array `field_schema` breaks that at
   * runtime, not at compile time.
   */
  field_schema: IntegrationFieldSchema[];
};

/**
 * One configured integration. A single type for three responses: the list
 * (`IntegrationDto`), the single GET (`IntegrationDetailDto`) and the upsert
 * (`IntegrationDto`).
 *
 * ⚠ DRIFT: the single GET has a second, narrower branch. When no row exists
 * for the provider it answers from the catalog instead, and that object omits
 * `notes`, `updated_at` and `updated_by` altogether while adding `is_new:
 * true`. `notes` is therefore optional here even though both generated DTOs
 * mark it required, and `is_new` is optional because neither the list nor the
 * upsert ever emits it.
 */
export type Integration = Omit<
  genSettings.IntegrationDto,
  'last_test_status' | 'notes' | 'updated_at' | 'updated_by'
> & {
  /**
   * ⚠ DRIFT: `integrations.last_test_status` is an unconstrained `text`
   * column, generated (correctly) as `string`. Only the test endpoint writes
   * it, and only ever `ok` or `error`; the catalog branch of the single GET
   * synthesises the third value, `untested`. Widened to `string` to match the
   * service: the console's `=== 'ok'` / `=== 'error'` / `=== 'untested'`
   * checks are unaffected.
   */
  last_test_status: string;
  notes?: string | null;
  /** Absent on the never-configured branch; `null | string` on the detail DTO. */
  updated_at?: string | null;
  updated_by?: string | null;
  /** Only ever `true`, and only from the never-configured branch of the single GET. */
  is_new?: boolean;
};

/**
 * Body of `PUT /v1/admin/integrations/{provider_code}`.
 *
 * `config` and `enabled` stay required: the generated
 * `genSettings.UpsertIntegrationRequest` marks every member optional because
 * the C# type is a mutable class with defaults, but an omitted `config` wipes
 * the stored credentials (`body.Config ?? new Dictionary<…>()`) and an omitted
 * `enabled` silently disables the row.
 */
export type UpsertIntegrationRequest = Omit<genSettings.UpsertIntegrationRequest, 'config' | 'enabled'> & {
  config: Record<string, unknown>;
  enabled: boolean;
};

/**
 * ⚠ DRIFT: `status` is the `last_test_status` column echoed back, i.e. an
 * unconstrained `string`, not the `'ok' | 'error'` union this module used to
 * declare. The stub probe only ever writes those two (`ok` when every required
 * field is populated), but nothing enforces it. Taken from the generated shape;
 * no call site narrows on it.
 */
export type IntegrationTestResult = genSettings.IntegrationTestResultDto;

export function listIntegrationProviders(): Promise<Envelope<IntegrationProvider[]>> {
  return http.get<Envelope<IntegrationProvider[]>>('/v1/admin/integrations/providers');
}

export function listIntegrations(opts?: { scope?: string; org_id?: string; reveal?: boolean }): Promise<Envelope<Integration[]>> {
  const q: Record<string, string | boolean> = {};
  if (opts?.scope) q.scope = opts.scope;
  if (opts?.org_id) q.org_id = opts.org_id;
  if (opts?.reveal) q.reveal = true;
  return http.get<Envelope<Integration[]>>('/v1/admin/integrations', Object.keys(q).length ? { query: q } : undefined);
}

export function getIntegration(providerCode: string, opts?: { org_id?: string; reveal?: boolean }): Promise<Envelope<Integration>> {
  const q: Record<string, string | boolean> = {};
  if (opts?.org_id) q.org_id = opts.org_id;
  if (opts?.reveal) q.reveal = true;
  return http.get<Envelope<Integration>>(`/v1/admin/integrations/${encodeURIComponent(providerCode)}`, Object.keys(q).length ? { query: q } : undefined);
}

export function upsertIntegration(providerCode: string, req: UpsertIntegrationRequest): Promise<Envelope<Integration>> {
  return http.put<Envelope<Integration>>(`/v1/admin/integrations/${encodeURIComponent(providerCode)}`, { body: req });
}

export function deleteIntegration(providerCode: string, opts?: { org_id?: string }): Promise<void> {
  const q = opts?.org_id ? { query: { org_id: opts.org_id } } : undefined;
  return http.delete<void>(`/v1/admin/integrations/${encodeURIComponent(providerCode)}`, q);
}

export function testIntegration(providerCode: string, opts?: { org_id?: string }): Promise<Envelope<IntegrationTestResult>> {
  const q = opts?.org_id ? { query: { org_id: opts.org_id } } : undefined;
  return http.post<Envelope<IntegrationTestResult>>(`/v1/admin/integrations/${encodeURIComponent(providerCode)}/test`, q);
}

// ── Invoices (W16 + W41 validation, billing) ──────────────────────────────
//
// The four unions below are `./billing.ts`'s: the same `services/billing`
// records back both surfaces, and `InvoiceStatus` / `InvoiceValidationStatus` /
// `InvoiceValidator` are all bare `string`s in the generated DTOs, pinned there
// against `InvoiceStatuses` / `InvoiceValidationStatuses` in
// `services/billing/Domain/Billing.cs`.

export type { InvoiceStatus };

/** List projection (`InvoiceShared.Summary`): no lines, no live verdict. */
export type AdminInvoice = Omit<
  genBilling.InvoiceSummaryDto,
  'status' | 'validation_status' | 'validation_validator'
> & {
  status: InvoiceStatus;
  /** W41: persisted validate-only verdict (see `billing.InvoiceValidation` for the live one). */
  validation_status: InvoiceValidationStatus;
  validation_validator: InvoiceValidator | null;
};

export type AdminInvoiceLine = genBilling.InvoiceLineDto;

/** Detail projection: the summary fields plus `lines` and a freshly computed `validation`. */
export type AdminInvoiceDetail = Omit<
  genBilling.InvoiceDetailDto,
  'status' | 'validation_status' | 'validation_validator' | 'validation'
> & {
  status: InvoiceStatus;
  validation_status: InvoiceValidationStatus;
  validation_validator: InvoiceValidator | null;
  /** Fresh SARS/FIRS verdict computed for this response (W41). */
  validation: InvoiceValidation;
};

export type AdminInvoiceValidateResult = Omit<
  genBilling.InvoiceValidateResultDto,
  'validation' | 'validation_status'
> & {
  validation: InvoiceValidation;
  validation_status: InvoiceValidationStatus;
};

/**
 * W41: re-run the validator for any tenant's invoice and persist the verdict.
 * `strict: true` turns an invalid verdict into HTTP 422 `INVOICE_INVALID`.
 */
export function validateInvoice(id: string, opts?: { strict?: boolean }): Promise<Envelope<AdminInvoiceValidateResult>> {
  return http.post<Envelope<AdminInvoiceValidateResult>>(
    `/v1/admin/invoices/${encodeURIComponent(id)}/validate`,
    opts?.strict ? { query: { strict: 'true' } } : undefined
  );
}

export function listInvoices(opts?: { org_id?: string; status?: InvoiceStatus; limit?: number }): Promise<Envelope<AdminInvoice[]>> {
  const q: Record<string, string | number> = {};
  if (opts?.org_id) q.org_id = opts.org_id;
  if (opts?.status) q.status = opts.status;
  if (opts?.limit !== undefined) q.limit = opts.limit;
  return http.get<Envelope<AdminInvoice[]>>('/v1/admin/invoices', Object.keys(q).length ? { query: q } : undefined);
}

export function getInvoice(id: string): Promise<Envelope<AdminInvoiceDetail>> {
  return http.get<Envelope<AdminInvoiceDetail>>(`/v1/admin/invoices/${encodeURIComponent(id)}`);
}

/** URL for `<a href>` / window.open: not a request. */
export function invoicePdfUrl(id: string): string {
  return `/v1/admin/invoices/${encodeURIComponent(id)}/pdf`;
}

export function regenerateInvoicePdf(id: string): Promise<Envelope<{ id: string; regenerated: boolean }>> {
  return http.post<Envelope<{ id: string; regenerated: boolean }>>(`/v1/admin/invoices/${encodeURIComponent(id)}/regenerate`);
}

/**
 * One line of {@link GenerateInvoiceRequest}. `description`, `quantity` and
 * `unit_amount_minor` stay required: the generated type marks them optional
 * only because the C# `GenerateInvoiceLine` is a mutable class with defaults,
 * and a line without them bills zero under a blank description.
 */
export type GenerateInvoiceLine = Omit<
  genBilling.GenerateInvoiceLine,
  'description' | 'quantity' | 'unit_amount_minor'
> & {
  description: string;
  quantity: number;
  unit_amount_minor: number;
};

/**
 * Body of `POST /v1/admin/invoices/generate` (201 Created).
 *
 * `organisation_id`, `period_start`, `period_end` and `lines` stay required:
 * the generated `genBilling.GenerateInvoiceRequest` marks every member
 * optional (mutable C# class, no `[Required]`), but the handler answers 400
 * `MISSING_ORG` without the first and 400 `NO_LINES` without the last, and the
 * two `DateOnly` period bounds default to `0001-01-01` when omitted.
 */
export type GenerateInvoiceRequest = Omit<
  genBilling.GenerateInvoiceRequest,
  'organisation_id' | 'period_start' | 'period_end' | 'lines' | 'customer_type'
> & {
  organisation_id: string;
  period_start: string;
  period_end: string;
  lines: GenerateInvoiceLine[];
  /**
   * `'business'` (default) | `'individual'`. Narrowed for editor help; the
   * column and the wire type are an open `string`, and only the FIRS
   * customer-TIN rule reads it.
   */
  customer_type?: 'business' | 'individual' | null;
};

export type GeneratedInvoice = Omit<
  genBilling.GeneratedInvoiceDto,
  'status' | 'validation_status' | 'validation'
> & {
  status: InvoiceStatus;
  validation_status: InvoiceValidationStatus;
  validation: InvoiceValidation;
};

export function generateInvoice(req: GenerateInvoiceRequest): Promise<Envelope<GeneratedInvoice>> {
  return http.post<Envelope<GeneratedInvoice>>('/v1/admin/invoices/generate', { body: req });
}

// ── Alerts dispatcher (W23, settings: fans out to PagerDuty + Slack) ──────

/**
 * Body of `POST /v1/admin/alerts/dispatch`. `summary` stays required (the
 * handler answers 400 `MISSING_SUMMARY` without it) even though the generated
 * `AlertRequest` marks it optional; `severity` is narrowed to the four words
 * the console offers and `AlertChannels` maps to a Slack icon: the wire type
 * is an open `string` that defaults to `warning`.
 */
export type AlertDispatchRequest = Omit<genSettings.AlertRequest, 'summary' | 'severity'> & {
  summary: string;
  severity?: 'info' | 'warning' | 'error' | 'critical';
};

/**
 * Per-channel outcome. Both fields are `string` on the wire; the unions are
 * pinned because every `ChannelOutcome` in `services/settings` is constructed
 * from these literals (`AdminAlertsEndpoints` for `skipped` /
 * `not_configured`, `AlertChannels` for `ok` / `error`).
 */
export type AlertChannelOutcome = Omit<genSettings.AlertChannelOutcomeDto, 'channel' | 'status'> & {
  /** `email` and `whatsapp` since 2 October 2026: every alert by email, critical ones by WhatsApp. */
  channel: 'pagerduty' | 'slack' | 'linear' | 'email' | 'whatsapp';
  status: 'ok' | 'error' | 'skipped' | 'not_configured' | 'skipped_resolved' | 'skipped_not_critical';
};

/**
 * Who production alerts go to (owner's decision of 2 October 2026): every alert to the `email`
 * addresses, critical ones also to the `whatsapp` number, sent from the platform's own number with
 * the approved `template` in `language`. Kept in the `ops.alerts.*` platform settings.
 */
export interface AlertRecipients {
  email: string[];
  whatsapp?: string | null;
  template: string;
  language: string;
}

/** Blank `email` or `whatsapp` turns that channel off. `email` takes several addresses separated by commas. */
export interface UpdateAlertRecipientsRequest {
  email?: string;
  whatsapp?: string;
  template?: string;
  language?: string;
}

export function getAlertRecipients(): Promise<Envelope<AlertRecipients>> {
  return http.get<Envelope<AlertRecipients>>('/v1/admin/alerts/recipients');
}

/** 400 `INVALID_EMAIL`, `INVALID_NUMBER` (not in international form) or `INVALID_TEMPLATE`. */
export function updateAlertRecipients(req: UpdateAlertRecipientsRequest): Promise<Envelope<AlertRecipients>> {
  return http.put<Envelope<AlertRecipients>>('/v1/admin/alerts/recipients', { body: req });
}

/**
 * ⚠ DRIFT: one type for two response shapes. `/dispatch` returns the
 * generated `AlertDispatchResultDto` (`summary` + `severity` + `channels`);
 * `/test` returns `AlertTestResultDto`, which is `channels` and nothing else.
 * Both are declared optional here so a single `AlertDispatchResult | null`
 * state can hold either, which is what the console's alerts page does
 * `summary` and `severity` are always present after a dispatch and always
 * absent after a test.
 */
export type AlertDispatchResult = Omit<
  genSettings.AlertDispatchResultDto,
  'summary' | 'severity' | 'channels'
> & {
  summary?: string;
  severity?: string;
  channels: AlertChannelOutcome[];
};

export function dispatchAlert(req: AlertDispatchRequest): Promise<Envelope<AlertDispatchResult>> {
  return http.post<Envelope<AlertDispatchResult>>('/v1/admin/alerts/dispatch', { body: req });
}

/**
 * The company's details the website, the terms and invoices show (2 October 2026). Also public, for the
 * website, at `GET /v1/public/site-company`. A blank phone hides the line on the website.
 */
export interface SiteCompany {
  legal_name: string;
  registration_number?: string | null;
  address?: string | null;
  /** International form. */
  phone?: string | null;
  /**
   * Whether the website shows each value (3 October 2026). The legal name is always shown. Invoices carry
   * every value whatever is chosen here. Leave one out of an update to keep its setting.
   */
  show_registration_number?: boolean;
  show_address?: boolean;
  show_phone?: boolean;
}

export function getSiteCompany(): Promise<Envelope<SiteCompany>> {
  return http.get<Envelope<SiteCompany>>('/v1/admin/content/company');
}

/** A field left out keeps its value; blank clears it. 400 `INVALID_PHONE` when the phone is not in international form. */
export function updateSiteCompany(req: Partial<SiteCompany>): Promise<Envelope<SiteCompany>> {
  return http.put<Envelope<SiteCompany>>('/v1/admin/content/company', { body: req });
}

export function testAlert(): Promise<Envelope<AlertDispatchResult>> {
  return http.post<Envelope<AlertDispatchResult>>('/v1/admin/alerts/test');
}

// ── API keys (identity, platform-side + cross-tenant) ─────────────────────

export type AdminApiKey = genIdentity.AdminApiKeyListItem;

export function listApiKeys(opts?: { org_id?: string; include_revoked?: boolean }): Promise<Envelope<AdminApiKey[]>> {
  const q: Record<string, string | boolean> = {};
  if (opts?.org_id) q.org_id = opts.org_id;
  if (opts?.include_revoked) q.include_revoked = true;
  return http.get<Envelope<AdminApiKey[]>>('/v1/admin/api-keys', Object.keys(q).length ? { query: q } : undefined);
}

export function revokeApiKey(id: string): Promise<void> {
  return http.delete<void>(`/v1/admin/api-keys/${encodeURIComponent(id)}`);
}

// ── Conversations admin (inbox, cross-tenant) ─────────────────────────────

export type AdminConversation = genInbox.AdminConversationDto;

/**
 * The endpoint also accepts a `state` filter
 * (`genInbox.InboxAdminListConversationsData['query']`); this client has never
 * sent it and does not start now.
 */
export function listConversations(opts?: { org_id?: string; limit?: number }): Promise<Envelope<AdminConversation[]>> {
  const q: Record<string, string | number> = {};
  if (opts?.org_id) q.org_id = opts.org_id;
  if (opts?.limit !== undefined) q.limit = opts.limit;
  return http.get<Envelope<AdminConversation[]>>('/v1/admin/conversations', Object.keys(q).length ? { query: q } : undefined);
}

// ── Broadcasts admin (broadcasts, cross-tenant) ───────────────────────────

/**
 * One broadcast across every organisation. The channel, what a person calls it (`name`: the email subject, the
 * SMS text or the template), how far it reached and why it failed as a whole were added on 4 October 2026, by
 * hand until the generator is next run against the service.
 */
export type AdminBroadcast = genBroadcasts.AdminBroadcastDto & {
  channel?: 'whatsapp' | 'sms' | 'email';
  name?: string | null;
  recipients_delivered?: number;
  recipients_read?: number;
  estimated_cost_minor?: number | null;
  failure_code?: string | null;
};

/** `GET /v1/admin/broadcasts` meta: `total` is every row under the filters, not the page. */
export interface AdminBroadcastPage {
  data: AdminBroadcast[];
  meta?: { count: number; total: number; limit: number; offset: number };
}

/**
 * Broadcasts across every organisation, newest first. Paged and filtered on the server (4 October 2026):
 * `status` is a status code, `channel` is `whatsapp`, `sms` or `email`.
 */
export function listBroadcasts(opts?: {
  org_id?: string;
  limit?: number;
  offset?: number;
  status?: string;
  channel?: string;
}): Promise<AdminBroadcastPage> {
  const q: Record<string, string | number> = {};
  if (opts?.org_id) q.org_id = opts.org_id;
  if (opts?.limit !== undefined) q.limit = opts.limit;
  if (opts?.offset !== undefined) q.offset = opts.offset;
  if (opts?.status) q.status = opts.status;
  if (opts?.channel) q.channel = opts.channel;
  return http.get<AdminBroadcastPage>('/v1/admin/broadcasts', Object.keys(q).length ? { query: q } : undefined);
}

// ── Ops: RabbitMQ queues, service health, backups (settings) ──────────────

/**
 * One queue as the RabbitMQ management API reports it, re-shaped by
 * `AdminOpsEndpoints`.
 *
 * ⚠ DRIFT: `name` and `vhost` are `null | string` in the generated
 * `QueueStatDto`: the handler reads them with `JsonElement.GetString()`, which
 * is nullable. Both are declared non-null here because the console
 * dereferences `name` (`r.name.toLowerCase()`, and it is the list's React
 * key); RabbitMQ always sends both, so this is a wire-shape technicality
 * rather than a live hazard.
 *
 * `messages`, `messages_ready` and `messages_unacked` are C# `long` (the
 * handler reads them with `GetInt64()`), so a queue deeper than 2^53 messages
 * would lose precision as a JS number. `consumers` is `int`.
 */
export type QueueStat = Omit<genSettings.QueueStatDto, 'name' | 'vhost'> & {
  name: string;
  vhost: string;
};

/**
 * ⚠ DRIFT: `status` is a bare `string` in the generated `ServiceHealthDto`.
 * Pinned to the three words `AdminOpsEndpoints` writes: `healthy` on a 2xx
 * probe, `unhealthy` on any other response, `down` when the probe throws.
 */
export type ServiceHealthRow = Omit<genSettings.ServiceHealthDto, 'status'> & {
  status: 'healthy' | 'unhealthy' | 'down';
};

/**
 * `GET /v1/admin/ops/queues` always answers 200; a RabbitMQ failure comes back
 * as an empty `data` plus `meta.error`. `meta` is `Partial<>` because the
 * generated `QueuesMeta` marks all four members required while the handler
 * sends `{count}` on success and `{error, status}` or `{error, message}` on
 * the two failure paths.
 */
export interface QueuesEnvelope extends Envelope<QueueStat[]> {
  meta?: Partial<genSettings.QueuesMeta>;
}

/**
 * `GET /v1/admin/ops/backups` - `data` is always empty today; the operator
 * instructions live in `meta` (generated as the required-member
 * `BackupListMeta`, kept `Partial<>` here so existing optional reads keep
 * compiling).
 *
 * The generated name was `BackupsMeta` until the settings service renamed it.
 * The SDK was not regenerated at the time, so this wrapper went on naming a
 * type the service had stopped emitting and `pnpm --filter
 * @repletoryreach/sdk-api typecheck` failed the moment anyone regenerated. The
 * service is the truth, so the wrapper follows it.
 */
export interface BackupsEnvelope extends Envelope<unknown[]> {
  meta?: Partial<genSettings.BackupListMeta>;
}

export function listQueues(): Promise<QueuesEnvelope> {
  return http.get<QueuesEnvelope>('/v1/admin/ops/queues');
}

export function listServiceHealth(): Promise<Envelope<ServiceHealthRow[]>> {
  return http.get<Envelope<ServiceHealthRow[]>>('/v1/admin/ops/health');
}

export function listBackups(): Promise<BackupsEnvelope> {
  return http.get<BackupsEnvelope>('/v1/admin/ops/backups');
}

// ── Media stats (media, cross-tenant) ─────────────────────────────────────

export type MediaStatsRow = genMedia.MediaStatsByTenantDto;
export type MediaStatsByMime = genMedia.MediaStatsByMimeDto;

/**
 * Platform-admin storage stats.
 *
 * ⚠ DRIFT (resolved by the cutover); this module's hand-written `MediaStats`
 * omitted `uploads_last_24h`, `bytes_last_24h` and `by_mime`, all of which
 * `/v1/admin/media/stats` returns, so it and `media.MediaStats` disagreed
 * about the same payload. Both now alias the generated `MediaStatsDto`; the
 * three fields are additive, so nothing that compiled before stops.
 *
 * The two functions still differ in the URL they call: `media.stats()` uses
 * the canonical `/v1/admin/stats/media`, this one the `/v1/admin/media/stats`
 * alias. Both are mapped and both return the same body.
 */
export type MediaStats = genMedia.MediaStatsDto;

export function getMediaStats(): Promise<Envelope<MediaStats>> {
  return http.get<Envelope<MediaStats>>('/v1/admin/media/stats');
}

// ── Webhook subscriptions (webhooks, cross-tenant) ────────────────────────

export type AdminWebhookSubscription = genWebhooks.AdminWebhookSubscriptionDto;
export type AdminWebhookSubscriptionState = genWebhooks.AdminWebhookSubscriptionStateDto;
export type AdminReplayResult = genWebhooks.AdminReplayResultDto;

export function listWebhookSubscriptions(opts?: { org_id?: string; limit?: number }): Promise<Envelope<AdminWebhookSubscription[]>> {
  const q: Record<string, string | number> = {};
  if (opts?.org_id) q.org_id = opts.org_id;
  if (opts?.limit !== undefined) q.limit = opts.limit;
  return http.get<Envelope<AdminWebhookSubscription[]>>('/v1/admin/webhooks/subscriptions', Object.keys(q).length ? { query: q } : undefined);
}

/**
 * Pause or resume a tenant's webhook subscription. Resuming also clears the
 * consecutive-failure count that auto-disabled it, because an operator turning
 * it back on is saying the endpoint is healthy again.
 *
 * There is deliberately no admin delete: pausing is reversible and is what an
 * incident needs, while destroying another organisation's integration is
 * theirs to decide.
 */
export function setWebhookSubscriptionActive(id: string, isActive: boolean): Promise<Envelope<AdminWebhookSubscriptionState>> {
  return http.patch<Envelope<AdminWebhookSubscriptionState>>(`/v1/admin/webhooks/subscriptions/${encodeURIComponent(id)}`, { body: { is_active: isActive } });
}

/** Requeue every failed or dead delivery for this subscription from the last 24 hours. */
export function replayWebhookSubscription(id: string): Promise<Envelope<AdminReplayResult>> {
  return http.post<Envelope<AdminReplayResult>>(`/v1/admin/webhooks/subscriptions/${encodeURIComponent(id)}/replay`, {});
}

/** Requeue one delivery attempt. The target safety check runs again before it fires. */
export function replayWebhookDelivery(id: string): Promise<Envelope<AdminReplayResult>> {
  return http.post<Envelope<AdminReplayResult>>(`/v1/admin/webhooks/deliveries/${encodeURIComponent(id)}/replay`, {});
}

// ── Plans + subscriptions (billing, cross-tenant) ─────────────────────────

/**
 * A plan as the platform console sees it. `markup_percent` is the plan's markup
 * over the platform's cost, which prices its messages; `included_ceiling_minor`
 * is the most included credits its fee can carry at that markup. Platform
 * console only: neither is ever on a customer-facing shape.
 */
export type AdminPlan = genBilling.AdminPlanDto & {
  markup_percent?: number;
  included_ceiling_minor?: number;
};

/** The same `SubscriptionDto` the tenant surface returns, listed across orgs. */
export type AdminSubscription = genBilling.SubscriptionDto & {
  ends_at?: string | null;
  pending_plan_id?: string | null;
  /** Plans are paid by card since 11 October 2026: whether a card is saved for this plan. */
  has_card?: boolean;
  /** The platform's own business, charged like any other (11 October 2026). */
  house_account?: boolean;
  /** Pays by invoice: the plan is paid from bought credit and changed here (18 October 2026). */
  pays_by_invoice?: boolean;
  /** Extra use is switched on and active, so the plan reads "Growth+" or "Scale+" (30 October 2026). */
  extra_use?: boolean;
};

export function listPlans(): Promise<Envelope<AdminPlan[]>> {
  return http.get<Envelope<AdminPlan[]>>('/v1/admin/plans');
}

/**
 * The words and allowances of a plan: name, tagline, badge, the feature lines
 * and the numeric limits (null is unlimited), sort order and whether it is
 * offered. Omitted fields are left alone; a blank tagline or badge clears it.
 * Prices are not edited: a new price is a new plan (404 `PLAN_NOT_FOUND`,
 * 400 `NAME_REQUIRED`).
 */
/**
 * `markup_percent` and `included_credits_minor` are guarded so no plan can lose
 * money: 422 `PLAN_CAN_LOSE_MONEY` when the credits exceed
 * `fee x (1 + markup)` (`ceiling_minor` in the body), 422 `MARKUP_LADDER` when a
 * paid plan would be dearer per message than the free one.
 */
export type UpdatePlanRequest = genBilling.UpdatePlanRequest & {
  markup_percent?: number;
  included_credits_minor?: number;
};

export function updatePlan(id: string, req: UpdatePlanRequest): Promise<Envelope<AdminPlan>> {
  return http.put<Envelope<AdminPlan>>(`/v1/admin/plans/${encodeURIComponent(id)}`, { body: req });
}

export function listSubscriptions(): Promise<Envelope<AdminSubscription[]>> {
  return http.get<Envelope<AdminSubscription[]>>('/v1/admin/subscriptions');
}

// ── Phone numbers + templates (channel-wa, cross-tenant) ──────────────────

export type AdminPhoneNumber = genChannelWa.AdminPhoneNumberDto & {
  /**
   * Who Meta bills (5 October 2026): `platform` (the shared account, paid in
   * credits) or `customer` (the business's own account, billed by Meta).
   * Hand-written until the channel-wa document is regenerated.
   */
  meta_billing_payer?: 'platform' | 'customer' | null;
  /** Meta's display-name review: `APPROVED`, `PENDING_REVIEW`, `DECLINED` and so on. Absent when never checked. */
  name_status?: string | null;
  name_rejection_reason?: string | null;
  /** A new name the business asked for, for the owner to make in WhatsApp Manager. */
  name_requested?: string | null;
  name_requested_at?: string | null;
  /** The email (or user id) of whoever asked. */
  name_requested_by?: string | null;
};

export type AdminTemplate = Omit<genChannelWa.AdminTemplateDto, 'status'> & {
  /**
   * ⚠ DRIFT: the service declares this `string`; the values come from the
   * `MessageTemplateStatuses` static class, which OpenAPI cannot see as an
   * enum. Narrowed to the same five lifecycle values `templates.ts` pins on
   * `MessageTemplate['status']`.
   */
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'paused';
};

export function listPhoneNumbers(opts?: { org_id?: string }): Promise<Envelope<AdminPhoneNumber[]>> {
  const q = opts?.org_id ? { query: { org_id: opts.org_id } } : undefined;
  return http.get<Envelope<AdminPhoneNumber[]>>('/v1/admin/numbers', q);
}

export function listAdminTemplates(opts?: { org_id?: string }): Promise<Envelope<AdminTemplate[]>> {
  const q = opts?.org_id ? { query: { org_id: opts.org_id } } : undefined;
  return http.get<Envelope<AdminTemplate[]>>('/v1/admin/templates', q);
}

// ── Content: the marketing site's news and updates (settings) ────────────

/**
 * A post as the console edits it. The body is Markdown; pictures and video
 * are `![caption](asset:ID)` and `!video[caption](url or asset:ID)`. Public
 * while `status` is `published` and `published_at` is not in the future.
 */
export type BlogPost = genSettings.BlogPostDto;
export type UpsertBlogPostRequest = genSettings.UpsertBlogPostRequest;
/** A picture or video uploaded for articles; refer to it as `asset:{id}`. */
export type BlogAsset = genSettings.BlogAssetDto;

export function listBlogPosts(opts?: { status?: 'draft' | 'published' }): Promise<Envelope<BlogPost[]>> {
  return http.get<Envelope<BlogPost[]>>('/v1/admin/content/posts', opts?.status ? { query: { status: opts.status } } : undefined);
}

export function getBlogPost(id: string): Promise<Envelope<BlogPost>> {
  return http.get<Envelope<BlogPost>>(`/v1/admin/content/posts/${encodeURIComponent(id)}`);
}

/** 400 `TITLE_REQUIRED` / `SLUG_INVALID`, 409 `SLUG_TAKEN`. The slug is made from the title when blank. */
export function createBlogPost(req: UpsertBlogPostRequest): Promise<Envelope<BlogPost>> {
  return http.post<Envelope<BlogPost>>('/v1/admin/content/posts', { body: req });
}

export function updateBlogPost(id: string, req: UpsertBlogPostRequest): Promise<Envelope<BlogPost>> {
  return http.put<Envelope<BlogPost>>(`/v1/admin/content/posts/${encodeURIComponent(id)}`, { body: req });
}

export function publishBlogPost(id: string): Promise<Envelope<BlogPost>> {
  return http.post<Envelope<BlogPost>>(`/v1/admin/content/posts/${encodeURIComponent(id)}/publish`, {});
}

export function unpublishBlogPost(id: string): Promise<Envelope<BlogPost>> {
  return http.post<Envelope<BlogPost>>(`/v1/admin/content/posts/${encodeURIComponent(id)}/unpublish`, {});
}

export function deleteBlogPost(id: string): Promise<void> {
  return http.delete<void>(`/v1/admin/content/posts/${encodeURIComponent(id)}`);
}

/** Pictures (PNG, JPEG, WebP, GIF, SVG) and video (MP4, WebM) up to 25 MB. 400 `FILE_TYPE` / `FILE_TOO_LARGE`. */
export function uploadBlogAsset(file: File): Promise<Envelope<BlogAsset>> {
  const form = new FormData();
  form.append('file', file, file.name);
  return http.post<Envelope<BlogAsset>>('/v1/admin/content/posts/assets', { body: form });
}

export function listBlogAssets(): Promise<Envelope<BlogAsset[]>> {
  return http.get<Envelope<BlogAsset[]>>('/v1/admin/content/posts/assets');
}

export function deleteBlogAsset(id: string): Promise<void> {
  return http.delete<void>(`/v1/admin/content/posts/assets/${encodeURIComponent(id)}`);
}

// ── Meta news feed (settings, 15 September 2026) ────────────────────────────
// What Meta changes on the WhatsApp Business Platform (pricing and rate cards
// above all, then API versions, deprecations and template rules), collected
// from feeds and documentation pages by the settings service on a timer.
//
// The service leaves null members out of its JSON, so a `null | string` member
// of the generated types can also arrive absent; read them with `??`.

/** Keyword topics an item can carry. */
export type MetaNewsTopic = 'pricing' | 'whatsapp' | 'api' | 'templates' | 'policy';

/** Which parser reads a source. */
export type MetaNewsSourceKind = 'atom' | 'rss' | 'page';

export type MetaNewsItem = genSettings.MetaNewsItemDto;
/** `{ data, unread_count, unread_pricing_count, meta: { page, page_size, total } }`. */
export type MetaNewsList = genSettings.MetaNewsListResponse;
export type MetaNewsSummary = genSettings.MetaNewsSummaryDto;
export type MetaNewsSource = genSettings.MetaNewsSourceDto;
export type MetaNewsRefreshResult = genSettings.MetaNewsRefreshResultDto;

export interface ListMetaNewsOptions {
  topic?: MetaNewsTopic;
  /** `true` unread only, `false` read only, absent for both. */
  unread?: boolean;
  /** A source id or code. */
  source?: string;
  page?: number;
  /** Default 50, at most 200. */
  page_size?: number;
}

/**
 * The generated `CreateMetaNewsSourceRequest` types every member `null | string`
 * because the server validates them itself; the console always sends all three.
 */
export interface CreateMetaNewsSourceRequest {
  name: string;
  /** A public https address. 400 `URL_NOT_HTTPS` / `URL_PRIVATE` / `URL_INVALID`, 409 `SOURCE_EXISTS`. */
  url: string;
  kind: MetaNewsSourceKind;
}

export interface UpdateMetaNewsSourceRequest {
  name?: string;
  enabled?: boolean;
}

/** Items newest first, with the platform-wide unread totals. 400 `TOPIC_UNKNOWN`. */
export function listMetaNews(opts?: ListMetaNewsOptions): Promise<MetaNewsList> {
  const q: Record<string, string | number | boolean> = {};
  if (opts?.topic) q.topic = opts.topic;
  if (opts?.unread != null) q.unread = opts.unread;
  if (opts?.source) q.source = opts.source;
  if (opts?.page != null) q.page = opts.page;
  if (opts?.page_size != null) q.page_size = opts.page_size;
  return http.get<MetaNewsList>('/v1/admin/meta-news', Object.keys(q).length ? { query: q } : undefined);
}

/** What a banner needs: unread totals, the last check and the newest unread pricing item (or null). */
export function getMetaNewsSummary(): Promise<Envelope<MetaNewsSummary>> {
  return http.get<Envelope<MetaNewsSummary>>('/v1/admin/meta-news/summary');
}

export function markMetaNewsRead(id: string): Promise<Envelope<MetaNewsItem>> {
  return http.post<Envelope<MetaNewsItem>>(`/v1/admin/meta-news/${encodeURIComponent(id)}/read`, {});
}

export function markMetaNewsUnread(id: string): Promise<Envelope<MetaNewsItem>> {
  return http.post<Envelope<MetaNewsItem>>(`/v1/admin/meta-news/${encodeURIComponent(id)}/unread`, {});
}

/** Every unread item, or only those with `topic`. */
export function markAllMetaNewsRead(opts?: { topic?: MetaNewsTopic }): Promise<Envelope<genSettings.MetaNewsReadAllResultDto>> {
  return http.post<Envelope<genSettings.MetaNewsReadAllResultDto>>(
    '/v1/admin/meta-news/read-all',
    opts?.topic ? { query: { topic: opts.topic } } : {}
  );
}

export function listMetaNewsSources(): Promise<Envelope<MetaNewsSource[]>> {
  return http.get<Envelope<MetaNewsSource[]>>('/v1/admin/meta-news/sources');
}

export function createMetaNewsSource(req: CreateMetaNewsSourceRequest): Promise<Envelope<MetaNewsSource>> {
  return http.post<Envelope<MetaNewsSource>>('/v1/admin/meta-news/sources', { body: req });
}

export function updateMetaNewsSource(id: string, req: UpdateMetaNewsSourceRequest): Promise<Envelope<MetaNewsSource>> {
  return http.put<Envelope<MetaNewsSource>>(`/v1/admin/meta-news/sources/${encodeURIComponent(id)}`, { body: req });
}

/** Deletes the source and every item found in it. */
export function deleteMetaNewsSource(id: string): Promise<void> {
  return http.delete<void>(`/v1/admin/meta-news/sources/${encodeURIComponent(id)}`);
}

/**
 * Checks now: every enabled source, or only `source_id` (409 `SOURCE_DISABLED`
 * when it is turned off). One result per source checked; a failed source has
 * `ok: false` and its reason in `error`.
 */
export function refreshMetaNews(opts?: { source_id?: string }): Promise<Envelope<MetaNewsRefreshResult[]>> {
  return http.post<Envelope<MetaNewsRefreshResult[]>>(
    '/v1/admin/meta-news/refresh',
    opts?.source_id ? { body: { source_id: opts.source_id } } : {}
  );
}

// ── partners: the operator's half of the refer-and-earn programme (W47) ─────
//
// A partner earns a share of what the platform keeps on the usage of the
// companies they introduced. Everything below is platform-admin only: the
// figures include the basis each share was worked out from, which the partner
// themselves is never shown, and the withdrawal queue carries the bank details
// an operator types into the bank.

export type AffiliateSummary = genBilling.AffiliateSummaryDto;
export type AdminAffiliate = genBilling.AdminAffiliateDto;
export type AdminAffiliateDetail = genBilling.AdminAffiliateDetailDto;
export type AdminReferral = genBilling.AdminReferralDto;
export type AdminEarning = genBilling.AdminEarningDto;
export type AdminWithdrawal = genBilling.AdminWithdrawalDto;
export type AffiliateSettings = genBilling.AffiliateSettingsDto;
export type AffiliateEvent = genBilling.AffiliateEventDto;
export type AccrualRun = genBilling.AccrualRunDto;

/** Active, waiting, suspended, what has been earned this month and what is owed. */
export function getAffiliateSummary(): Promise<Envelope<AffiliateSummary>> {
  return http.get<Envelope<AffiliateSummary>>('/v1/admin/affiliates/summary');
}

export function listAffiliates(opts?: {
  status?: string; q?: string; limit?: number; cursor?: string;
}): Promise<Envelope<AdminAffiliate[]>> {
  return http.get<Envelope<AdminAffiliate[]>>('/v1/admin/affiliates', { query: { ...opts } });
}

/** The whole of one partner: profile, balances, referrals with their flags, earnings, withdrawals and the reasons behind every action. */
export function getAffiliate(id: string): Promise<Envelope<AdminAffiliateDetail>> {
  return http.get<Envelope<AdminAffiliateDetail>>(`/v1/admin/affiliates/${encodeURIComponent(id)}`);
}

/** Approves an application and issues the code. 409 `INVALID_TRANSITION` for a partner who is already active or closed. */
export function approveAffiliate(id: string, reason?: string): Promise<Envelope<{ id: string; status: string; code: string }>> {
  return http.post<Envelope<{ id: string; status: string; code: string }>>(
    `/v1/admin/affiliates/${encodeURIComponent(id)}/approve`, { body: { reason } });
}

/** Stops accrual and withdrawals. Nothing is forfeited: reactivating catches up the days in between. A reason is required. */
export function suspendAffiliate(id: string, reason: string): Promise<Envelope<{ id: string; status: string }>> {
  return http.post<Envelope<{ id: string; status: string }>>(
    `/v1/admin/affiliates/${encodeURIComponent(id)}/suspend`, { body: { reason } });
}

export function reactivateAffiliate(id: string, reason?: string): Promise<Envelope<{ id: string; status: string }>> {
  return http.post<Envelope<{ id: string; status: string }>>(
    `/v1/admin/affiliates/${encodeURIComponent(id)}/reactivate`, { body: { reason } });
}

/** 409 `BALANCE_OUTSTANDING` while the partner is still owed anything or has a withdrawal waiting. */
export function closeAffiliate(id: string, reason: string): Promise<Envelope<{ id: string; status: string; referrals_revoked: number }>> {
  return http.post<Envelope<{ id: string; status: string; referrals_revoked: number }>>(
    `/v1/admin/affiliates/${encodeURIComponent(id)}/close`, { body: { reason } });
}

/**
 * Sets one partner's share and term. A change applies to referrals made from
 * then on, because an existing referral keeps the rate it was made at; pass
 * `apply_to_existing` to rewrite those too, which is recorded.
 */
export function updateAffiliate(id: string, body: {
  commission_percent?: number; commission_months?: number | null;
  clear_rate?: boolean; apply_to_existing?: boolean; admin_notes?: string; reason?: string;
}): Promise<Envelope<{ id: string; commission_percent: number | null; commission_months: number | null; existing_updated: number }>> {
  return http.patch<Envelope<{ id: string; commission_percent: number | null; commission_months: number | null; existing_updated: number }>>(
    `/v1/admin/affiliates/${encodeURIComponent(id)}`, { body });
}

/** Attaches a company to a partner by hand. Refused with 409 `TOO_LATE` once that company has paid anything. */
export function attachReferral(id: string, body: {
  organisation_id: string; reason: string; referred_name?: string;
}): Promise<Envelope<AdminReferral>> {
  return http.post<Envelope<AdminReferral>>(`/v1/admin/affiliates/${encodeURIComponent(id)}/referrals`, { body });
}

/** Stops future earning on one referral. What was already earned stays earned. */
export function revokeReferral(referralId: string, reason: string): Promise<Envelope<{ id: string; status: string }>> {
  return http.post<Envelope<{ id: string; status: string }>>(
    `/v1/admin/affiliates/referrals/${encodeURIComponent(referralId)}/revoke`, { body: { reason } });
}

/** Corrects a partner's balance, either way, with a reason. Available at once. */
export function adjustAffiliate(id: string, body: { amount_minor: number; reason: string }):
  Promise<Envelope<{ pending_minor: number; available_minor: number }>> {
  return http.post<Envelope<{ pending_minor: number; available_minor: number }>>(
    `/v1/admin/affiliates/${encodeURIComponent(id)}/adjustments`, { body });
}

/** Runs the accrual pass now rather than waiting for the sweep. Changes nothing about what is counted. */
export function runAffiliateAccrual(affiliateId?: string): Promise<Envelope<AccrualRun>> {
  return http.post<Envelope<AccrualRun>>('/v1/admin/affiliates/run-accrual', {
    query: { affiliate_id: affiliateId },
  });
}

export function listAffiliateWithdrawals(opts?: { status?: string; limit?: number; cursor?: string }):
  Promise<Envelope<AdminWithdrawal[]>> {
  return http.get<Envelope<AdminWithdrawal[]>>('/v1/admin/affiliates/withdrawals', { query: { ...opts } });
}

export function approveAffiliateWithdrawal(id: string, note?: string): Promise<Envelope<genBilling.WithdrawalDto>> {
  return http.post<Envelope<genBilling.WithdrawalDto>>(
    `/v1/admin/affiliates/withdrawals/${encodeURIComponent(id)}/approve`, { body: { reason: note } });
}

/** Refuses a withdrawal and puts the money back. The reason is quoted to the partner word for word. */
export function rejectAffiliateWithdrawal(id: string, reason: string): Promise<Envelope<genBilling.WithdrawalDto>> {
  return http.post<Envelope<genBilling.WithdrawalDto>>(
    `/v1/admin/affiliates/withdrawals/${encodeURIComponent(id)}/reject`, { body: { reason } });
}

/**
 * Records that a withdrawal was paid, with the bank reference that matches it
 * to a statement line. 409 `BALANCE_NEGATIVE` when a reversal has since taken
 * the partner's available earnings below zero.
 */
export function markAffiliateWithdrawalPaid(id: string, body: {
  payment_reference: string; paid_at?: string; note?: string;
}): Promise<Envelope<genBilling.WithdrawalDto>> {
  return http.post<Envelope<genBilling.WithdrawalDto>>(
    `/v1/admin/affiliates/withdrawals/${encodeURIComponent(id)}/mark-paid`, { body });
}

/**
 * Where the approved withdrawals are downloaded as a bank file. Needs the admin token, so open it with a fetch and not a link.
 * It is the only place a partner's full account number is given out (everywhere else it is masked), and each
 * download is recorded in every listed partner's history as `bank_file_downloaded`.
 */
export function affiliateWithdrawalsCsvPath(status = 'approved'): string {
  return `/v1/admin/affiliates/withdrawals/export.csv?status=${encodeURIComponent(status)}`;
}

export function getAffiliateSettings(): Promise<Envelope<AffiliateSettings>> {
  return http.get<Envelope<AffiliateSettings>>('/v1/admin/affiliates/settings');
}

/** Editing the terms text moves the version, so what a partner accepted stays meaningful. */
export function updateAffiliateSettings(body: {
  programme_enabled?: boolean; auto_approve?: boolean; default_percent?: number;
  default_months?: number | null; clear_months?: boolean; clearance_days?: number;
  min_withdrawal_minor?: number; late_code_days?: number; terms_markdown?: string;
}): Promise<Envelope<AffiliateSettings>> {
  return http.put<Envelope<AffiliateSettings>>('/v1/admin/affiliates/settings', { body });
}

// ── AI models (14 October 2026) ─────────────────────────────────────────────

/** A model the AI API can offer, on or off; prices are in the price book under its SKUs. */
export interface AdminAiModel {
  code: string;
  kind: 'text' | 'speech' | 'transcribe' | 'image';
  name: string;
  description?: string | null;
  provider: string;
  /** The Bedrock model id or inference profile; empty means the platform's Bedrock text model. */
  model_id: string;
  /** Null means the platform's Bedrock region. */
  region?: string | null;
  in_sku?: string | null;
  out_sku?: string | null;
  unit_sku?: string | null;
  accepts_images: boolean;
  max_output_tokens: number;
  /** The model's context window in tokens; 0 when not known (read as 200 000). Suggest flows with AI keeps each call under half of it. */
  context_tokens: number;
  outside_south_africa: boolean;
  enabled: boolean;
  sort_order: number;
  updated_at: string;
  updated_by?: string | null;
  calls_last30_days: number;
  charged_last30_days_minor: number;
  /** Speech to text: the S3 bucket recordings wait in while they are transcribed. */
  storage_bucket?: string | null;
}

export interface UpdateAiModelRequest {
  enabled?: boolean;
  name?: string;
  description?: string;
  model_id?: string;
  region?: string;
  accepts_images?: boolean;
  max_output_tokens?: number;
  /** 4 000 to 2 000 000, or 0 for not known. */
  context_tokens?: number;
  outside_south_africa?: boolean;
  sort_order?: number;
  /** Speech to text only; a transcribe model cannot be switched on without one. Empty clears it. */
  storage_bucket?: string;
}

export interface AiModelTest {
  ok: boolean;
  answer?: string | null;
  model_id?: string | null;
  input_tokens: number;
  output_tokens: number;
  milliseconds: number;
  stub: boolean;
  error?: string | null;
}

export function listAiModels(): Promise<Envelope<AdminAiModel[]>> {
  return http.get<Envelope<AdminAiModel[]>>('/v1/admin/ai/models');
}

export function updateAiModel(code: string, req: UpdateAiModelRequest): Promise<Envelope<AdminAiModel>> {
  return http.put<Envelope<AdminAiModel>>(`/v1/admin/ai/models/${encodeURIComponent(code)}`, { body: req });
}

/**
 * Proves a model works before it is switched on; the platform pays. Text: a one-word answer. Speech: a short sentence
 * read aloud. Transcribe: a two-second recording stored, transcribed and deleted. Image: one picture (about R0,68).
 */
export function testAiModel(code: string): Promise<Envelope<AiModelTest>> {
  return http.post<Envelope<AiModelTest>>(`/v1/admin/ai/models/${encodeURIComponent(code)}/test`, {});
}

// ── Languages (1 November 2026) ─────────────────────────────────────────────
//
// The language catalogue every chatbot speaks from (flows-llm). A business offers its customers any language switched
// on here, and `GET /v1/languages` (`flows.listLanguages()`) lists those. The built-in lines are the chatbot's own
// fixed wording (the language menu, "Switch to isiZulu?", the hand-over notices, ...): English and Afrikaans are
// written in the code; every other language is machine-translated once, at the platform's cost, and can be corrected
// and marked as read by a native speaker here.

/** How far a language's built-in lines are. `translated` counts the lines it has from the current English. */
export interface AdminLanguageWordingSummary {
  total: number;
  translated: number;
  edited: number;
  reviewed: number;
  /** Edited lines whose English has changed since they were edited. */
  stale: number;
}

/** A language as the platform owner manages it: `GET /v1/admin/languages`. */
export interface AdminLanguage {
  code: string;
  /** The English name ("Zulu"), 1 to 60 characters. */
  name: string;
  /** The language's own name ("isiZulu"), 1 to 20 characters, as it goes on a WhatsApp button. */
  native_name: string;
  region: 'south_africa' | 'africa' | 'widely_used' | string;
  sort_order: number;
  /** Meta's WhatsApp template language codes (`zu`, `en_ZA`, `pt_BR`); empty when templates cannot be in it. */
  meta_codes: string[];
  /** AWS Transcribe's code (`zu-ZA`); null when voice notes in it cannot be turned into words. */
  speech_code?: string | null;
  /** Words that bring the language menu back, in this language ("ulimi", "shintsha ulimi"). */
  change_words: string[];
  greeting_words: string[];
  yes_words: string[];
  no_words: string[];
  /** strong | good | review: how well the AI writes it; review means a native speaker should read the fixed wording. */
  quality: 'strong' | 'good' | 'review' | string;
  /** Off: no business can offer it, and `/v1/languages` leaves it out. English cannot be switched off. */
  enabled: boolean;
  /** How many chatbots offer it. Reads 0 in the answer to an update; read the list again for the count. */
  bots_offering: number;
  wordings: AdminLanguageWordingSummary;
  updated_at: string;
  updated_by?: string | null;
}

/** `PUT /v1/admin/languages/{code}`. Absent fields are kept; a word list replaces the whole list. */
export interface UpdateAdminLanguageRequest {
  name?: string;
  native_name?: string;
  meta_codes?: string[];
  /** Empty clears it. */
  speech_code?: string;
  change_words?: string[];
  greeting_words?: string[];
  yes_words?: string[];
  no_words?: string[];
  quality?: 'strong' | 'good' | 'review';
  enabled?: boolean;
  sort_order?: number;
}

/**
 * One built-in line in one language, beside the English (and the Afrikaans). `source`: `built_in` (English and
 * Afrikaans, written in the code and not editable here), `machine`, `edited` or `missing` (the English is used until it
 * is translated). `stale`: an edited line whose English has changed since. `max_length` is set for a button line.
 */
export interface LanguageWording {
  key: string;
  english: string;
  afrikaans: string;
  text?: string | null;
  source: 'built_in' | 'machine' | 'edited' | 'missing' | string;
  reviewed: boolean;
  stale: boolean;
  max_length?: number | null;
  note?: string | null;
}

/**
 * `PUT /v1/admin/languages/{code}/wordings/{key}`. A blank `text` removes the edit (the machine translation comes back
 * on the next run). Refused with 400 when a `{{...}}` part of the English is missing, or a button line is too long.
 */
export interface UpdateLanguageWordingRequest {
  text?: string;
  reviewed?: boolean;
}

export interface TranslateLanguageWordingsResult {
  written: number;
}

/** Every language in the catalogue, on or off, with how many chatbots offer it and how far its built-in lines are. */
export function listAdminLanguages(): Promise<Envelope<AdminLanguage[]>> {
  return http.get<Envelope<AdminLanguage[]>>('/v1/admin/languages');
}

/** Switch a language on or off, or change its names, words and codes. English cannot be switched off (400). */
export function updateAdminLanguage(code: string, req: UpdateAdminLanguageRequest): Promise<Envelope<AdminLanguage>> {
  return http.put<Envelope<AdminLanguage>>(`/v1/admin/languages/${encodeURIComponent(code)}`, { body: req });
}

/** The chatbot's built-in lines in one language, beside the English. */
export function listLanguageWordings(code: string): Promise<Envelope<LanguageWording[]>> {
  return http.get<Envelope<LanguageWording[]>>(`/v1/admin/languages/${encodeURIComponent(code)}/wordings`);
}

/** Correct one built-in line, or mark it as read by a native speaker. English and Afrikaans are refused (400). */
export function updateLanguageWording(code: string, key: string, req: UpdateLanguageWordingRequest): Promise<Envelope<LanguageWording>> {
  return http.put<Envelope<LanguageWording>>(
    `/v1/admin/languages/${encodeURIComponent(code)}/wordings/${encodeURIComponent(key)}`,
    { body: req }
  );
}

/**
 * Translate the lines a language lacks now; the platform pays (a few cents a language). `all` remakes every machine
 * line as well; lines the owner edited are kept.
 */
export function translateLanguageWordings(code: string, opts: { all?: boolean } = {}): Promise<Envelope<TranslateLanguageWordingsResult>> {
  return http.post<Envelope<TranslateLanguageWordingsResult>>(
    `/v1/admin/languages/${encodeURIComponent(code)}/wordings/translate`,
    { body: opts.all ? { all: true } : {} }
  );
}
